import { Bone, Matrix4, Quaternion, Vector3 } from "three";

import { Basis, lookAtRight, quaternionAngle } from "../misc/util";

import {
  GeneralConstraint,
  swingTwistDecomposition,
} from "./constraint/general";
import { Joint } from "./joint";
import { PointType } from "./metadata";

const tempM = new Matrix4();
const tempV: Vector3 = new Vector3();

const tempTwist = new Quaternion();
const tempSwing = new Quaternion();

const tempBoneQ = new Quaternion();
const tempDir = new Vector3();

const parentQ = new Quaternion();

const yAxis = new Vector3(0, 1, 0);

export class Edge {
  name: string;

  start: Joint;
  end: Joint;

  constraintsActive = true;

  qPrev = new Quaternion();

  private _bones: OffsetBone[] = [];

  private _candidateStartPos: Vector3 = new Vector3();
  private _candidateIsSet = false;

  // basis is mainly used for debug, just a direction vector would be enough
  private _basis: Basis;

  private _q: Quaternion = new Quaternion();
  private _qInv: Quaternion = new Quaternion();
  private _qLocal: Quaternion = new Quaternion();
  private _qInit: Quaternion = new Quaternion();

  private _edgeLen = 0;

  private _constraints: GeneralConstraint[] = [];

  constructor(start: Joint, end: Joint) {
    this.name = end?.name ?? "not-set";

    this.start = start;
    this.start.addChildEdge(this);

    this.end = end;
    this.end.setParentEdge(this);

    this._edgeLen = this.start.pos.distanceTo(this.end.pos);

    this._basis = new Basis(start.pos);

    // NB: update should calculate q for start and end points
    //     we save the offset if the bone q is different
    this.update();
    this._qInit.copy(this._q);

    this.initQLocal();
  }

  addBone(bone: Bone, qOffset = true, posOffset = true, localSpace = false) {
    // TODO: pull out magic numbers in this function
    bone.getWorldDirection(tempDir);
    bone.getWorldQuaternion(tempBoneQ);

    const basisDiff = this._basis.right.angleTo(tempDir.negate());

    const offsetRotation =
      this.end.pos.equals(this.start.pos) || basisDiff > 0.1;

    let boneQOffset: Quaternion | undefined = undefined;

    if (offsetRotation && qOffset) {
      boneQOffset = new Quaternion();
    } else {
      this._q.copy(tempBoneQ);
    }

    this._qInit.copy(this._q);
    this.updateQuaternions();

    let relPos: Vector3 | undefined = new Vector3();
    bone.getWorldPosition(relPos);
    if (
      (!localSpace && !posOffset) ||
      this.start.pos.distanceTo(relPos) < 0.00001
    ) {
      relPos = undefined;
    } else {
      relPos.sub(this.start.pos).applyQuaternion(this._qInv);
    }

    if (boneQOffset) {
      boneQOffset.multiplyQuaternions(this._qInv, tempBoneQ);
      const angle = quaternionAngle(boneQOffset);
    }

    this._bones.push({
      bone: bone,
      qOffset: boneQOffset,
      posOffset: relPos,
      localSpace: localSpace,
    });

    this.initQLocal();
  }

  get bones(): OffsetBone[] {
    return this._bones;
  }

  get candidateStartPos(): Vector3 {
    return this._candidateStartPos;
  }

  set candidateStartPos(toCopy: Vector3) {
    this._candidateStartPos.copy(toCopy);
    this._candidateIsSet = true;
  }

  get hasCandidateStartPos(): boolean {
    return this._candidateIsSet;
  }

  get edgeLen(): number {
    return this._edgeLen;
  }

  get q(): Quaternion {
    return this._q;
  }

  get qInv(): Quaternion {
    return this._qInv;
  }

  get qInit(): Quaternion {
    return this._qInit;
  }

  get qLocalInit(): Quaternion {
    return this._qLocal;
  }

  get basis(): Basis {
    return this._basis;
  }

  get constraints(): GeneralConstraint[] {
    return this._constraints;
  }

  candidateStartPosReset() {
    this._candidateIsSet = false;
  }

  addConstraint(constraint: GeneralConstraint, child: Edge): boolean {
    for (const c of this._constraints) {
      if (c.child == child) {
        console.warn(
          "behaviour currently unknown for multiple constraints " +
            `on same edge pairs (${this.name} -> ${child.name})`
        );
      }
    }

    if (!constraint.bind(this, child)) {
      return false;
    }

    this._constraints.push(constraint);
    return true;
  }

  hasConstraints(): boolean {
    return this.constraintsActive && this._constraints.length != 0;
  }

  applyConstraints(backward: boolean, child?: Edge): boolean {
    if (!this.constraintsActive) {
      return false;
    }

    let constraints: GeneralConstraint[] = child
      ? this._constraints.filter((c) => c.child == child)
      : this._constraints;

    if (backward) {
      constraints = constraints.filter(
        (c) => c.child && c.child.end.props.type != PointType.KABSCH
      );
    }

    if (constraints.length == 0) {
      return false;
    }
    if (constraints.length == 1) {
      // don't need to combine effects
      return constraints[0].apply(backward);
    }

    // collect effects and take average - in an attempt to best
    // satisfy all constraints
    // TODO: currently just averaging swing - is this enough?
    let success = true;

    let toMove: Joint | undefined;
    let toMoveFirst: Joint | undefined;
    let movingEdge: Edge | undefined;

    const totalTwist = { angle: 0 };

    for (let i = 0; i < constraints.length; i++) {
      const constraint = constraints[i];
      if (!constraint.apply(backward, tempV, totalTwist)) {
        success = false;
      }

      if (backward) {
        toMove = constraint.child?.end;
        movingEdge = constraint.child;
      } else {
        toMove = constraint.parent?.start;
        movingEdge = constraint.parent;
      }

      if (!toMoveFirst && toMove) {
        toMoveFirst = toMove;
        toMove.pos.set(0, 0, 0);
      }

      if (!toMove || toMove != toMoveFirst) {
        console.warn(
          `invalid movable joint in constraint on ${this.name}`,
          toMove,
          toMoveFirst
        );
      } else {
        toMove.pos.add(tempV);
      }
    }

    if (toMoveFirst && movingEdge) {
      toMoveFirst.pos.divideScalar(constraints.length);
      movingEdge.update();

      totalTwist.angle /= constraints.length;

      // extract twist and change angle
      swingTwistDecomposition(movingEdge._q, tempSwing, tempTwist);

      tempTwist.setFromAxisAngle(yAxis, totalTwist.angle);

      movingEdge._q.multiplyQuaternions(tempSwing, tempTwist);
      movingEdge.updateQuaternions();
    } else {
      success = false;
    }

    return success;
  }

  update(): boolean {
    // TODO: is this right?
    const up = this._basis.fwd;

    lookAtRight(tempM, this.end.pos, this.start.pos, up);

    this._q.setFromRotationMatrix(tempM);
    this.updateQuaternions();

    return true;
  }

  updateQuaternions() {
    this._qInv.copy(this._q).invert();
    this._basis.setFromQuaternion(this._q);
  }

  updateBones() {
    for (const boneWrapper of this._bones) {
      const { bone, qOffset, posOffset, localSpace } = boneWrapper;
      bone.quaternion.copy(this._q);
      if (qOffset) {
        bone.quaternion.multiply(qOffset);
      }

      if (localSpace && bone.parent) {
        bone.parent.getWorldQuaternion(parentQ);
        parentQ.invert();

        bone.quaternion.premultiply(parentQ);
      }

      // TODO: refactor the localSpace hack for the head into something
      //       nicer
      // NB: localSpace is a nasty way to piggy back on three.js
      //     bones and use the local space quaternion setting
      //     it doesn't support position setting
      if (!localSpace) {
        if (posOffset) {
          bone.position.copy(posOffset).applyQuaternion(this._q);
          bone.position.add(this.start.pos);
        } else {
          bone.position.copy(this.start.pos);
        }
      }
    }

    for (const child of this.end.childEdges) {
      child.updateBones();
    }
  }

  moveBones(delta: Vector3) {
    for (const boneWrapper of this._bones) {
      const { bone, localSpace } = boneWrapper;

      // ignore the head - posed differently
      if (!localSpace) {
        bone.position.add(delta);
      }
    }

    for (const child of this.end.childEdges) {
      child.moveBones(delta);
    }
  }

  private initQLocal() {
    this._qLocal.copy(this._q);
    if (this.start.parentEdge) {
      this._qLocal.premultiply(this.start.parentEdge._qInv);
    }
  }
}

interface OffsetBone {
  bone: Bone;
  posOffset?: Vector3;
  qOffset?: Quaternion;
  localSpace?: boolean;
}
